import React from 'react';
import { 
  FileText, Home, Droplets, Flame, Zap, Building2, TreeDeciduous,
  CheckCircle, AlertTriangle, Clock, Camera, Calendar, DollarSign,
  Shield, TrendingUp, ClipboardList, Settings, Refrigerator
} from 'lucide-react';

const HomeProfilePreview = () => {
  const categories = [
    { name: 'Kitchen', score: 85, status: 'good', icon: Flame },
    { name: 'Bathrooms', score: 72, status: 'fair', icon: Droplets },
    { name: 'Electrical', score: 90, status: 'good', icon: Zap },
    { name: 'Roof & Exterior', score: 58, status: 'attention', icon: Building2 },
    { name: 'HVAC', score: 80, status: 'good', icon: Flame },
    { name: 'Landscaping', score: 75, status: 'fair', icon: TreeDeciduous },
  ];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getBarColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'good':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Good</span>;
      case 'fair':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Fair</span>;
      case 'attention':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Needs Attention</span>;
      default:
        return null;
    }
  };

  const includedItems = [
    { icon: ClipboardList, title: 'Condition Summary', desc: 'Room-by-room assessment' },
    { icon: Settings, title: 'Appliance Inventory', desc: 'Brand & model numbers' },
    { icon: Camera, title: 'Photo Documentation', desc: '50+ detailed photos' },
    { icon: AlertTriangle, title: 'Priority Issues', desc: 'What needs attention now' },
    { icon: Calendar, title: 'Maintenance Timeline', desc: '12-month action plan' },
    { icon: Shield, title: 'Safety Check', desc: 'Critical safety items' },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">
            Your Personalized Home Profile
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            More than an inspection report — it's a living document that helps you understand, maintain, and plan for your home's future.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Side - Report Mockup */}
          <div className="relative">
            {/* Report Card */}
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Report Header */}
              <div className="bg-[#003366] text-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Home className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">HOME PROFILE REPORT</h3>
                      <p className="text-white/70 text-sm">Valenrock Home Services</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white/70 text-xs">Report Date</p>
                    <p className="font-semibold">Dec 15, 2024</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-white/70 text-xs mb-1">Property Address</p>
                  <p className="font-semibold">123 Oak Street, Summit, NJ 07901</p>
                </div>
              </div>

              {/* Home Score Section */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-[#003366] flex items-center gap-2">
                    <TrendingUp size={18} />
                    Overall Home Score
                  </h4>
                  <span className="text-xs text-gray-500">Based on 47 inspection points</span>
                </div>
                
                {/* Score Circle */}
                <div className="flex items-center gap-6">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#e5e7eb"
                        strokeWidth="12"
                        fill="none"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#003366"
                        strokeWidth="12"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${78 * 3.52} 352`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-[#003366]">78</span>
                      <span className="text-xs text-gray-500">out of 100</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        <span className="text-sm text-gray-700">23 items in good condition</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="text-yellow-500" size={16} />
                        <span className="text-sm text-gray-700">8 items to monitor</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="text-red-500" size={16} />
                        <span className="text-sm text-gray-700">3 priority items</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="p-6">
                <h4 className="font-bold text-[#003366] mb-4 flex items-center gap-2">
                  <FileText size={18} />
                  Category Breakdown
                </h4>
                <div className="space-y-3">
                  {categories.map((cat, idx) => {
                    const Icon = cat.icon;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon size={16} className="text-[#003366]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(cat.status)}
                              <span className={`text-sm font-bold ${getScoreColor(cat.score)}`}>
                                {cat.score}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getBarColor(cat.score)} rounded-full transition-all`}
                              style={{ width: `${cat.score}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Appliance Inventory Preview */}
              <div className="p-6 border-t border-gray-100">
                <h4 className="font-bold text-[#003366] mb-4 flex items-center gap-2">
                  <Settings size={18} />
                  Appliance Inventory
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Flame size={14} className="text-[#003366]" />
                      <span className="text-gray-700">HVAC System</span>
                    </div>
                    <span className="text-gray-500 text-xs">Carrier 24ACC636 • 2019</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Droplets size={14} className="text-[#003366]" />
                      <span className="text-gray-700">Water Heater</span>
                    </div>
                    <span className="text-gray-500 text-xs">Rheem XG50 • 2021</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Refrigerator size={14} className="text-[#003366]" />
                      <span className="text-gray-700">Refrigerator</span>
                    </div>
                    <span className="text-gray-500 text-xs">Samsung RF28R7351 • 2022</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3 italic">+ boilers, toilets, dishwashers, and more...</p>
              </div>

              {/* Sample Callout */}
              <div className="mx-6 mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Priority Item</p>
                    <p className="text-amber-700 text-sm">Roof shingles showing wear on south-facing slope. Recommend inspection within 6 months.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#CFB973]/20 rounded-full blur-2xl -z-10"></div>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#003366]/10 rounded-full blur-2xl -z-10"></div>
          </div>

          {/* Right Side - What's Included */}
          <div className="space-y-6">
            <div className="bg-[#003366] text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-2">What's Included</h3>
              <p className="text-white/70 mb-6">Every Home Profile report includes:</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {includedItems.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-3 bg-white/10 rounded-lg p-4">
                      <div className="w-10 h-10 bg-[#CFB973] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-[#003366]" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{item.title}</p>
                        <p className="text-white/70 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Key Benefits */}
            <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-100">
              <h3 className="text-xl font-bold text-[#003366] mb-4">Why It Matters</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700"><strong>No surprises</strong> — know what's coming before it becomes urgent</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700"><strong>Smart budgeting</strong> — plan for repairs on your timeline</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700"><strong>Living document</strong> — updates as your home evolves</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700"><strong>One partner</strong> — we coordinate everything for you</span>
                </li>
              </ul>
            </div>

            {/* Sample badge */}
            <div className="text-center">
              <span className="inline-block px-4 py-2 bg-[#CFB973]/20 text-[#003366] rounded-full text-sm font-medium">
                ✨ Sample report shown — yours will be customized to your home
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProfilePreview;
